import { useState } from 'react';
import {
    Container,
    Segment,
    Header,
    Dropdown,
    Table,
    Message,
    Button,
} from 'semantic-ui-react';
import { useQuery, gql } from '@apollo/client';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { InterimStates } from '../../../components/InterimStates';
import { useIsAdmin } from '../../../hooks/isAdmin';
import { useBlocks } from '../../../hooks/useBlocks';
import { GET_TAGS } from '../../../queries/tags';

import { BlockAttendanceGraph, BlockAttendanceDay, DroppedStudentReport } from './BlockAttendanceGraph';
import styles from './BlockAttendance.module.css';

const GET_BLOCK_TAGS = gql`
  query BlockTags {
    blockTags {
      id
      name
    }
  }
`;

const GET_BLOCK_ATTENDANCE_REPORT = gql`
  query BlockAttendanceReport($blockTagId: Int!, $filters: String) {
    blockAttendanceReport(blockTagId: $blockTagId, filters: $filters) {
      days {
        date
        presentCount
        absentCount
        otherCount
        attendancePercentage
      }
      droppedStudents {
        userId
        name
        identifier
        phone
        courseId
        courseName
        courseSKU
        lastSessionDate
      }
    }
  }
`;

const BLOCK_STORAGE_KEY = 'blockAttendance_blockTagId';
const TAGS_STORAGE_KEY = 'blockAttendance_tagIds';

const formatLastSession = (iso: string | null) =>
    iso ? moment(iso).format('D MMM YYYY') : '-';

const BlockAttendance = () => {
    const isAdmin = useIsAdmin();
    const { currentBlockTagId } = useBlocks();

    const [blockTagId, setBlockTagId] = useState<number | null>(() => {
        try {
            const stored = localStorage.getItem(BLOCK_STORAGE_KEY);
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });
    const [tagIds, setTagIds] = useState<string[]>(() => {
        try {
            const stored = localStorage.getItem(TAGS_STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch {
            return [];
        }
    });

    // Default to the organisation's current block until the user picks one.
    const selectedBlockTagId = blockTagId ?? currentBlockTagId;

    const { loading: blocksLoading, error: blocksError, data: blocksData } = useQuery(GET_BLOCK_TAGS);
    const { loading: tagsLoading, error: tagsError, data: tagsData } = useQuery(GET_TAGS, {
        variables: {
            showPeopleTags: false,
            showCourseTags: true,
        }
    });
    const { loading, error, data } = useQuery(GET_BLOCK_ATTENDANCE_REPORT, {
        variables: {
            blockTagId: selectedBlockTagId,
            filters: tagIds.length > 0 ? tagIds.join(',') : '',
        },
        skip: selectedBlockTagId == null,
        notifyOnNetworkStatusChange: true,
    });

    if (blocksLoading || blocksError) {
        return <InterimStates loading={blocksLoading} error={blocksError} label='blocks' />;
    }

    if (!isAdmin) return null;

    const days: BlockAttendanceDay[] = data?.blockAttendanceReport?.days ?? [];
    const droppedStudents: DroppedStudentReport[] =
        [...(data?.blockAttendanceReport?.droppedStudents ?? [])].sort((a, b) => {
            // Reverse-chronological by lastSessionDate, nulls last.
            if (a.lastSessionDate == null && b.lastSessionDate == null) return 0;
            if (a.lastSessionDate == null) return 1;
            if (b.lastSessionDate == null) return -1;
            return moment(b.lastSessionDate).valueOf() - moment(a.lastSessionDate).valueOf();
        });

    const csvData = droppedStudents.map((s) => ({
        name: s.name ?? '',
        identifier: s.identifier ?? '',
        phone: s.phone ?? '',
        course: s.courseName ?? '',
        sku: s.courseSKU ?? '',
        lastSessionDate: s.lastSessionDate ? moment(s.lastSessionDate).format('YYYY-MM-DD') : '',
    }));
    const csvHeaders = [
        { label: 'Name', key: 'name' },
        { label: 'Identifier', key: 'identifier' },
        { label: 'Phone', key: 'phone' },
        { label: 'Course', key: 'course' },
        { label: 'SKU', key: 'sku' },
        { label: 'Last Session Date', key: 'lastSessionDate' },
    ];

    return (
        <ResponsiveContainer>
            <Segment style={{ padding: '2em 0em' }} vertical>
                <Container>
                    <Header as='h1' textAlign='center'>Block Attendance</Header>
                    <Dropdown
                        placeholder='Block'
                        fluid
                        selection
                        search
                        value={selectedBlockTagId ?? undefined}
                        onChange={(_, { value }) => {
                            const next = value as number;
                            setBlockTagId(next);
                            localStorage.setItem(BLOCK_STORAGE_KEY, JSON.stringify(next));
                        }}
                        options={
                            (blocksData?.blockTags ?? []).map((b: any) => ({
                                key: b.id,
                                text: b.name,
                                value: b.id,
                            }))
                        }
                    />
                    <Dropdown
                        placeholder='Tags'
                        fluid
                        multiple
                        search
                        selection
                        value={tagIds}
                        onChange={(_, { value }) => {
                            const next = value as string[];
                            setTagIds(next);
                            localStorage.setItem(TAGS_STORAGE_KEY, JSON.stringify(next));
                        }}
                        options={
                            tagsLoading || tagsError ? [] :
                                (tagsData && tagsData.tags || [])
                                    .map((p: any) => ({
                                        key: p.id,
                                        text: p.name,
                                        value: `tagId:${p.id}`,
                                    }))
                        }
                        style={{ marginTop: '0.5em' }}
                    />
                </Container>

                {/* Graph is full viewport width — blocks run ~12 weeks and need the room. */}
                <div className={styles.fullWidth}>
                    {selectedBlockTagId == null ? (
                        <Container>
                            <Message info content='Select a block to see its attendance.' />
                        </Container>
                    ) : loading ? (
                        <Container>
                            <Message icon='circle notched' content='Loading block attendance...' />
                        </Container>
                    ) : error ? (
                        <Container>
                            <Message negative content='Error loading the block attendance report.' />
                        </Container>
                    ) : days.length === 0 ? (
                        <Container>
                            <Message content='No sessions found for this block and tag selection.' />
                        </Container>
                    ) : (
                        <BlockAttendanceGraph days={days} droppedStudents={droppedStudents} />
                    )}
                </div>

                {selectedBlockTagId != null && !loading && !error && (
                    <Container>
                        <Header as='h2'>
                            Dropped students ({droppedStudents.length})
                        </Header>
                        {droppedStudents.length === 0 ? (
                            <Message content='No dropped students for this block and tag selection.' />
                        ) : (
                            <>
                                <Button basic as={CSVLink}
                                    data={csvData}
                                    headers={csvHeaders}
                                    filename='dropped-students.csv'
                                >
                                    ↓ Download CSV
                                </Button>
                                <Table celled>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell>Person</Table.HeaderCell>
                                            <Table.HeaderCell>Phone</Table.HeaderCell>
                                            <Table.HeaderCell>Course</Table.HeaderCell>
                                            <Table.HeaderCell>SKU</Table.HeaderCell>
                                            <Table.HeaderCell>Last session</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {droppedStudents.map((s) => (
                                            <Table.Row key={`${s.userId}-${s.courseId}`}>
                                                <Table.Cell>
                                                    <Link to={`/people/${s.userId}`}>{s.name} ({s.identifier})</Link>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {s.phone ? <a href={`tel:${s.phone}`}>{s.phone}</a> : '-'}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Link to={`/classes/${s.courseId}`}>{s.courseName}</Link>
                                                </Table.Cell>
                                                <Table.Cell>{s.courseSKU}</Table.Cell>
                                                <Table.Cell>{formatLastSession(s.lastSessionDate)}</Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            </>
                        )}
                    </Container>
                )}
            </Segment>
        </ResponsiveContainer>
    );
};

export { BlockAttendance };
